<script>
  // packages
  import { geoIdentity, geoPath } from 'd3-geo';
  import { pointer } from 'd3-selection';
  import debounce from 'lodash.debounce';
  import { onMount } from 'svelte';

  // components
  import Tooltip from './Tooltip.svelte';
  import TooltipRow from './TooltipRow.svelte';

  // utils
  import calculateHeightRatio from '../utils/calculate-height-ratio.js';
  import createSearchIndex from '../utils/create-search-index.js';
  import scaleCanvas from '../utils/scale-canvas.js';
  import { getColor } from '../colors.js';

  // public
  export let maxHeight = 500;
  /** @type {GeoJSON.FeatureCollection} */
  export let geography;
  /** @type {GeoJSON.FeatureCollection} */
  export let roads;
  /** @type {GeoJSON.FeatureCollection<GeoJSON.Point>} */
  export let cities;
  /** @type {(feature: GeoJSON.Feature) => string} */
  export let getFillStyle;
  /** @type {(feature?: GeoJSON.Feature) => import('../types.d').SubunitInterface | undefined} */
  export let findRaceData;

  // private
  const projection = geoIdentity().reflectY(true);
  const svgPath = geoPath(projection);
  const canvasPath = geoPath(projection);
  const heightRatio = calculateHeightRatio(geography.bounds);
  const isHorizontal = heightRatio < 1;
  const margin = isHorizontal ? 20 : 5;
  const findPolygon = createSearchIndex(geography.bounds.features);

  /** @type {number} */
  let width;
  /** @type {number} */
  let height;
  /** @type {HTMLDivElement} */
  let container;
  /** @type {SVGSVGElement} */
  let svg;
  /** @type {HTMLCanvasElement} */
  let canvas;
  /** @type {CanvasRenderingContext2D} */
  let context;
  /** @type {number} */
  let x;
  /** @type {number} */
  let y;
  let places = [];
  let highlightedFeature;

  $: tooltipRace = findRaceData(highlightedFeature);
  
  onMount(() => {
    context = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
    canvasPath.context(context);

    setDimensions();
    drawCanvas();
  });

  function drawCanvas() {
    if (!context) return;
    // empty out the rect
    context.clearRect(0, 0, width, height);

    for (let idx = 0; idx < geography.bounds.features.length; idx++) {
      const feature = geography.bounds.features[idx];

      const fillStyle = getFillStyle(feature);

      context.beginPath();
      canvasPath(feature);
      context.fillStyle = fillStyle;
      context.fill();
      context.closePath();
    }

    roads.bounds.features.forEach((/** @type {GeoJSON.Feature} */ feature) => {
      context.beginPath();
      canvasPath(feature);

      context.lineCap = 'round';
      context.lineJoin = 'round';

      if (feature.properties.scalerank <= 3) {
        context.strokeStyle = 'rgba(0, 0, 0, .12)';
        context.lineWidth = 1.2;
      } else {
        context.strokeStyle = 'rgba(0, 0, 0, .08)';
        context.lineWidth = 0.8;
      }

      context.stroke();
      context.closePath();
    });

    context.beginPath();
    canvasPath(geography.borders);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = 'rgba(255, 255, 255, 0.65)';
    context.lineWidth = 0.6;
    context.stroke();
    context.closePath();

    cities.bounds.features.forEach((/** @type {GeoJSON.Point} */ city) => {
      const point = projection(city.geometry.coordinates);

      context.beginPath();
      context.arc(
        point[0],
        point[1],
        city.properties.is_capital ? 4 : 2,
        0,
        Math.PI * 2,
        true
      );
      context.strokeStyle = 'rgba(33, 33, 33, .75)';
      context.lineWidth = 1.25;
      context.stroke();
      context.closePath();
    });
  }

  function setDimensions() {
    const parent = container.parentElement;

    if (!parent) {
      return;
    }

    const bounds = parent.getBoundingClientRect();

    const _width = bounds.width;
    const _height = _width * heightRatio;

    if (_height > maxHeight) {
      width = maxHeight / heightRatio;
      height = maxHeight;
    } else {
      width = 325;
      height = _height;
    }

    projection.fitSize(
      [width - margin - margin, height - margin - margin],
      geography.bounds
    );

    places = cities.bounds.features.map((feature) => {
      const properties = feature.properties;
      return {
        point: projection(feature.geometry.coordinates).map((c) => c + margin),
        label: feature.properties.city,
        dx: properties.dx,
        dy: properties.dy,
        textAnchor: properties.text_anchor,
      };
    });

    // we break out of Svelte here because we cannot pre-set this
    // div
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;

    // svg
    svg.setAttribute('width', width.toString());
    svg.setAttribute('height', height.toString());
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

    // canvas
    scaleCanvas(context, width, height, margin);
  }

  // events
  function onMouseMove(/** @type {MouseEvent} */ e) {
    const coord = pointer(e);

    // invert the coords using our projection
    const coords = /** @type {[number, number]} */ (
      projection.invert([coord[0] - margin, coord[1] - margin])
    );

    // find it
    const active = findPolygon(coords);

    if (active) {
      highlightedFeature = active;
    } else {
      highlightedFeature = undefined;
    }

    x = e.clientX;
    y = e.clientY;
  }

  function onMouseOut() {
    highlightedFeature = undefined;
  }

  const onResize = debounce(function onResize() {
    setDimensions();
    drawCanvas();
  }, 300);

  $: if (getFillStyle) {
    drawCanvas();
  }
</script>

<svelte:window on:resize|passive={onResize} />

<div
  class="box-border position-relative mx-auto mt-1"
  bind:this={container}
  on:mousemove={onMouseMove}
  on:mouseout={onMouseOut}
  on:blur={onMouseOut}
>
  <svg
    style="position: relative; top: 0; left: 0; z-index: 2;"
    bind:this={svg}
    preserveAspectRatio="xMidYMid"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <g class="places" pointer-events="none">
      {#each places as place (place.label)}
        <text
          class="place-label place-label--background"
          transform="translate({place.point})"
          dx={place.dx}
          dy={place.dy}
          text-anchor={place.textAnchor}
        >
          {place.label}
        </text>
        <text
          class="place-label place-label--foreground"
          transform="translate({place.point})"
          dx={place.dx}
          dy={place.dy}
          text-anchor={place.textAnchor}
        >
          {place.label}
        </text>
      {/each}
    </g>
    <g pointer-events="none" transform={`translate(${margin},${margin})`}>
      {#if highlightedFeature}
        <path
          stroke="#292929"
          stroke-width="2"
          fill="none"
          d={svgPath(highlightedFeature)}
        />
      {/if}
    </g>
  </svg>
  <canvas
    style="position: absolute; top: 0; left: 0; z-index: 1; width: 100%; height: 100%;"
    bind:this={canvas}
  />
</div>

{#if tooltipRace}
  <Tooltip {x} {y}>
    <div class="contest">
      <table class="contest__candidates contest__candidates--tooltip">
        <caption>
          <p class="contest__title">{tooltipRace.name}</p>
          <div class="contest__metrics">
            <p class="contest__precincts">
              {tooltipRace.eevp}% of expected vote in
            </p>
          </div>
        </caption>
        <thead>
          <tr>
            <th
              scope="col"
              class="contest__candidate-header whitespace-no-wrap"
            >
            Candidate
            </th>
            {#if tooltipRace.includeParty}
              <th
                scope="col"
                class="contest__candidate-header whitespace-no-wrap"
              >
                Party
              </th>
            {/if}
            <th
              scope="col"
              class="contest__candidate-header text-right whitespace-no-wrap"
            >
            %
          </th>
            <th
              scope="col"
              class="contest__candidate-header text-right whitespace-no-wrap total"
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {#if tooltipRace.candidates.length > 0}
            {#each tooltipRace.candidates as candidate (candidate.ap_id)}
              <TooltipRow
                name={candidate.name}
                color={getColor(candidate.ap_id)}
                party={candidate.party}
                votes={candidate.votes}
                votePercent={candidate.votePercent}
                includeParty={tooltipRace.includeParty}
              />
            {/each}
          {:else}
            <tr>
              <th
                class="candidate__cell candidate__cell-empty"
                scope="col"
                colSpan={4}
              >
                No results
              </th>
            </tr>
          {/if}
        </tbody>
      </table>
    </div>
  </Tooltip>
{/if}
